import React, { memo, useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";
import { setChartList } from "../../model/slice/chartSettingsSlice.js";
import { useDetectVesselConnection } from "../../hooks/useDetectVesselConnection.js";
import { NoData } from "../offlineComponent/noData.jsx";
import Offline from "../offlineComponent/offline.jsx";
import PropTypes from "prop-types";

import AmCharts from "./amcharts.jsx";
import { electricTrendLines } from "../../constants/trendGraph/electricLines.js";
import { setChartsSettings } from "../../model/slice/chartsSlice.js";

const disableUserSelect = {
  userSelect: "none",
  MozUserSelect: "none",
  WebkitUserSelect: "none",
  msUserSelect: "none",
};

const styles = {
  brush: {
    fontSize: "0px",
    position: "absolute",
    background: "#21262f",
    height: "20px",
    padding: "4px",
    "& rect": {
      stroke: "none",
      height: "20px",
      fillOpacity: "unset",
      fill: "#1b212a",
      rx: "2px",
    },
  },
  root: {},
  circle: {
    margin: "20px 20px",
    position: "relative",
    left: "calc(50% - 20px)",
    top: "calc(50% - 20px)",
  },
  noData: {
    color: "#FFFFFF",
  },
  containerRoot: {
    width: "100%",
    height: "100%",
    border: "solid white",
  },
  labelText: {
    "& tspan": {
      ...disableUserSelect,
    },
  },
};

const TrendGraph = memo((props) => {
  const { 
    geNumber = 0,
    sgNumber = 0,
    graphData,
    graphDataDigital,
    vesselId,
    type,
    chartType,
    isComparison,
    vessel,
    customChartLines,
    customLineData,
    isMockPreview,
    expand,
    comparisonGraphIndex,
    isVesselOverview,
    setChartRef,
    overviewTrendIdx,
  } = props;
  const dispatch = useDispatch();

  // electric chart settings
  useEffect(() => {
    const nameLists = [...new Array(geNumber)]
      .map((_, i) => `dg${i + 1}`)
      .concat([...new Array(sgNumber)].map((_, i) => `sg${i + 1}`));
    if (!nameLists.length) {
      return;
    }

    dispatch(
      setChartList(
        electricTrendLines?.filter(
          (d) =>
            d.name === "dateTime" || d.name === "localDateTime" || nameLists.includes(d?.keyName)
        )
      )
    );
  }, [geNumber, sgNumber]);


  return (
    <AmCharts
      graphData={graphData}
      graphDataDigital={graphDataDigital}
      vesselId={vesselId}
      type={type}
      chartType={chartType}
      isComparison={isComparison}
      vesselData={vessel}
      customChartLines={customChartLines}
      customLineData={customLineData}
      isMockPreview={isMockPreview}
      expand={expand}
      multipleGraphIndex={comparisonGraphIndex}
      isVesselOverview={isVesselOverview}
      setChartRef={setChartRef}
      overviewTrendIdx={overviewTrendIdx}
    />);
});

const DisplaySituation = React.memo((props) => {
  const {
    isInitialLoad,
    graphData = [],
    graphDataDigital = [],
    vesselId,
    isGraphDataLoading,
    height,
    isMockPreview = false,
    isNoData,
    geNumber,
    sgNumber,
    type,
    chartType,
    isComparison,
    vessel,
    customChartLines,
    customLineData,
    expand,
    comparisonGraphIndex,
    isVesselOverview,
    setChartRef,
    overviewTrendIdx,
    isTimeout,
  } = props;
  const { connections } = useDetectVesselConnection();
  
  switch (true) {
    case isTimeout:
      return (
        <Box component={"div"} sx={{ height: `${height}px` }}>
          <NoData />
        </Box>
      );
    case isInitialLoad || isGraphDataLoading:
      return (
        <Box component={"div"} sx={{ height: `100%` }}>
          <CircularProgress sx={styles.circle} />
        </Box>
      );
    case isNoData:
      return (
        <Box component={"div"} sx={{ height: `${height}px` }}>
          <NoData />
        </Box>
      );
    case graphData?.length > 0 || graphDataDigital?.length > 0 || isMockPreview:
      return <TrendGraph 
          geNumber={geNumber}
          sgNumber={sgNumber}
          graphData={graphData}
          graphDataDigital={graphDataDigital}
          vesselId={vesselId}
          type={type}
          chartType={chartType}
          isComparison={isComparison}
          vessel={vessel}
          customChartLines={customChartLines}
          customLineData={customLineData}
          isMockPreview={isMockPreview}
          expand={expand}
          comparisonGraphIndex={comparisonGraphIndex}
          isVesselOverview={isVesselOverview}
          setChartRef={setChartRef}
          overviewTrendIdx={overviewTrendIdx}
        />;
    case connections.includes(vesselId):
      return (
        <Box component={"div"} sx={{ height: `${height}px` }}>
          <Offline />
        </Box>
      );
    default:
      return (
        <Box component={"div"} sx={{ height: `100%` }}>
          <CircularProgress sx={styles.circle} />
        </Box>
      );
  }
});

export const Chart = React.memo((props) => {
  const {
    isVesselOverview,
    customChartLines,
    isInitialLoad,
    graphData = [],
    graphDataDigital = [],
    vesselId,
    isGraphDataLoading,
    height,
    isMockPreview = false,
    isNoData,
    geNumber,
    sgNumber,
    type,
    chartType,
    isComparison,
    vessel,
    customLineData,
    expand,
    comparisonGraphIndex,
    setChartRef,
    overviewTrendIdx,
    isTimeout,
  } = props;
  const dispatch = useDispatch();
  useEffect(() => {
    if (!isVesselOverview) {
      dispatch(setChartsSettings(customChartLines));
    }
  }, [dispatch, isVesselOverview, customChartLines]);
  return <DisplaySituation 
    isVesselOverview={isVesselOverview}
    customChartLines={customChartLines}
    isInitialLoad={isInitialLoad}
    graphData={graphData}
    graphDataDigital={graphDataDigital}
    vesselId={vesselId}
    isGraphDataLoading={isGraphDataLoading}
    height={height}
    isMockPreview={isMockPreview}
    isNoData={isNoData}
    geNumber={geNumber}
    sgNumber={sgNumber}
    type={type}
    chartType={chartType}
    isComparison={isComparison}
    vessel={vessel}
    customLineData={customLineData}
    expand={expand}
    comparisonGraphIndex={comparisonGraphIndex}
    setChartRef={setChartRef}
    overviewTrendIdx={overviewTrendIdx}
    isTimeout={isTimeout}
  />;
});

Chart.propTypes = {
  expand: PropTypes.string,
  type: PropTypes.string,
  isNoData: PropTypes.bool,
  graphData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  graphDataDigital: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  enginePosition: PropTypes.string,
  chartType: PropTypes.string,
  vesselId: PropTypes.string,
  geNumber: PropTypes.number,
  sgNumber: PropTypes.number,
  isInitialLoad: PropTypes.bool,
  isComparison: PropTypes.bool,
  comparisonGraphIndex: PropTypes.number,
  vessel: PropTypes.object,
  isVesselOverview: PropTypes.bool,
  customChartLines: PropTypes.array,
  isGraphDataLoading: PropTypes.bool,
  height: PropTypes.string,
  isMockPreview: PropTypes.bool,
  customLineData: PropTypes.array,
  setChartRef: PropTypes.func,
  overviewTrendIdx: PropTypes.number,
  isTimeout: PropTypes.bool,
};

DisplaySituation.propTypes = {
  expand: PropTypes.string,
  type: PropTypes.string,
  isNoData: PropTypes.bool,
  graphData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  graphDataDigital: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  enginePosition: PropTypes.string,
  chartType: PropTypes.string,
  vesselId: PropTypes.string,
  geNumber: PropTypes.number,
  sgNumber: PropTypes.number,
  isInitialLoad: PropTypes.bool,
  isComparison: PropTypes.bool,
  comparisonGraphIndex: PropTypes.number,
  vessel: PropTypes.object,
  isVesselOverview: PropTypes.bool,
  customChartLines: PropTypes.array,
  isGraphDataLoading: PropTypes.bool,
  height: PropTypes.string,
  isMockPreview: PropTypes.bool,
  customLineData: PropTypes.array,
  setChartRef: PropTypes.func,
  overviewTrendIdx: PropTypes.number,
  isTimeout: PropTypes.bool,
}

TrendGraph.propTypes = {
  expand: PropTypes.string,
  type: PropTypes.string,
  graphData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  graphDataDigital: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  chartType: PropTypes.string,
  vesselId: PropTypes.string,
  geNumber: PropTypes.number,
  sgNumber: PropTypes.number,
  isComparison: PropTypes.bool,
  comparisonGraphIndex: PropTypes.number,
  vessel: PropTypes.object,
  isVesselOverview: PropTypes.bool,
  customChartLines: PropTypes.array,
  isMockPreview: PropTypes.bool,
  customLineData: PropTypes.array,
  setChartRef: PropTypes.func,
  overviewTrendIdx: PropTypes.number,
}